
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { waitlistFormSchema, WaitlistFormValues } from "@/components/waitlist/schema";
import { useAuth } from "@/components/auth/AuthProvider";
import { useWaitlistPreferences } from "@/hooks/use-waitlist-preferences";

interface UseWaitlistFormProps {
  countyName?: string;
  nursingHomeName?: string;
}

export function useWaitlistForm({ countyName, nursingHomeName }: UseWaitlistFormProps) {
  const { user } = useAuth();
  const { savedPreferences } = useWaitlistPreferences();
  const [formInitialized, setFormInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      preferredLocation: countyName ? [countyName.toLowerCase().replace(/\s+/g, '-')] : [],
      specificNursingHome: nursingHomeName || "",
      careNeeds: [],
      careNeedsDetails: "",
      mobilityRequirements: "",
      residentRelation: "parent",
      residentSalutation: undefined,
      residentLastName: "",
      residentAge: "",
      timeframe: "researching",
      fairDealScheme: "need-info",
      contactPreference: "email",
      additionalInformation: "",
      consentToPrivacyPolicy: false,
    },
  });

  // Initialize form with saved preferences or user data
  useEffect(() => {
    if (formInitialized || !user) return;

    // Load saved preferences if available
    if (savedPreferences) {
      Object.entries(savedPreferences).forEach(([key, value]) => {
        if (key in form.getValues()) {
          const formKey = key as keyof WaitlistFormValues;
          form.setValue(formKey, value);
        }
      });
      
      toast.info("Your previous preferences have been restored");
      setFormInitialized(true);
      return;
    }

    // Fall back to user metadata if no saved preferences
    const userMeta = user.user_metadata;
    if (userMeta) {
      form.setValue("firstName", userMeta.first_name || "");
      form.setValue("lastName", userMeta.last_name || "");
      form.setValue("email", user.email || "");
      form.setValue("phone", userMeta.phone || "");
    }
    
    setFormInitialized(true);
  }, [user, form, formInitialized, savedPreferences]);

  return {
    form,
    isSubmitting,
    setIsSubmitting,
    user
  };
}
